import React, { ReactNode, useMemo } from "react";
import { findDepthExceedingParentGroupIds } from "@common/constants/domains/accounting/accountGroups";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { buildGroupPathSegments } from "./buildGroupPathSegments";
import { buildGroupTree } from "./buildGroupTree";
import { GroupPathLabel } from "./GroupPathLabel";
import {
    DEPTH_LIMIT_TOOLTIP,
    NO_GROUP_LABEL,
    NO_GROUP_VALUE,
} from "./GroupSelectField.constants";
import {
    filterGroupsBySelectableType,
    resolveSelectableGroupType,
} from "./selectableGroupType";
import styles from "./GroupSelectField.module.scss";

interface Props {
    parentGroupFieldName: string;
    groups: AccountGroup[];
    type: string | null;
    value: string | null;
    onChange: (parentGroupId: string | null) => void;
    label: string;
    description?: string;
    placeholder?: string;
    disabled?: boolean;
    // Pass for group-parent selection; accounts do not add a group level.
    isGroupParentPicker?: boolean;
    excludeIds?: string[];
    // Edited group whose subtree counts toward the depth limit.
    selfId?: string | null;
}

export const GroupSelectField: React.FC<Props> = ({
    parentGroupFieldName,
    groups,
    type,
    value,
    onChange,
    label,
    description,
    placeholder,
    disabled = false,
    isGroupParentPicker = false,
    excludeIds,
    selfId = null,
}) => {
    const selectedType = resolveSelectableGroupType(type);
    const groupsOfType = useMemo(
        () => filterGroupsBySelectableType(groups, type),
        [groups, type],
    );

    const treeItems = useMemo<Array<PickerItemData<string>>>(() => {
        const noGroupNode: PickerItemData<string> = {
            value: NO_GROUP_VALUE,
            label: NO_GROUP_LABEL,
        };
        if (!selectedType) {
            return [noGroupNode];
        }
        const disabledReasonByGroupId = new Map<string, string>();
        if (isGroupParentPicker) {
            for (const groupId of findDepthExceedingParentGroupIds({
                groups,
                movedGroupId: selfId,
            })) {
                disabledReasonByGroupId.set(groupId, DEPTH_LIMIT_TOOLTIP);
            }
        }
        return [
            noGroupNode,
            ...buildGroupTree(
                groupsOfType,
                excludeIds,
                disabledReasonByGroupId,
            ),
        ];
    }, [
        groups,
        groupsOfType,
        selectedType,
        excludeIds,
        selfId,
        isGroupParentPicker,
    ]);

    const treeValue = value ?? NO_GROUP_VALUE;

    const handleChange = (next: string | null): void => {
        if (next === null || next === NO_GROUP_VALUE) {
            onChange(null);
            return;
        }
        onChange(next);
    };

    const renderTriggerLabel = (
        selectedValues: readonly string[],
    ): ReactNode => {
        const [selectedValue] = selectedValues;
        if (selectedValue === NO_GROUP_VALUE) {
            return NO_GROUP_LABEL;
        }
        const segments = buildGroupPathSegments(groupsOfType, selectedValue);
        if (segments.length <= 1) {
            return segments[0] ?? "";
        }
        return <GroupPathLabel segments={segments} />;
    };

    return (
        <FormikField
            name={parentGroupFieldName}
            label={label}
            labelTooltip={description}
            control="button"
            className={styles.field}
        >
            <Picker.Select
                items={treeItems}
                value={treeValue}
                onValueChange={handleChange}
                disabled={disabled || !selectedType}
                searchable
                autoHighlight
                placeholder={placeholder}
                renderValue={renderTriggerLabel}
                defaultExpandAll
                selectableParents
                trigger={<Picker.Button variant="primary" size="xl" block />}
            />
        </FormikField>
    );
};
